/**
 * Form validation utilities
 */

export interface ValidationResult {
	valid: boolean;
	errors: Record<string, string>;
}

// Valid enum values
export const VALID_INQUIRY_TYPES = ['training', 'dev-services', 'partnership', 'other'] as const;
export const VALID_EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export const VALID_PROJECT_TYPES = ['product', 'web-app', 'internal-tool', 'ai-integration', 'other'] as const;

// Field length constraints
export const MAX_NAME_LENGTH = 100;
export const MAX_MESSAGE_LENGTH = 5000;
export const MAX_EMAIL_LENGTH = 254;

export function validateEmail(email: string): boolean {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email) && email.length <= MAX_EMAIL_LENGTH;
}

export function validateRequired(value: string, minLength: number = 1, maxLength: number = MAX_MESSAGE_LENGTH): boolean {
	const trimmed = value.trim();
	// Check that trimmed value has content and meets length requirements
	return trimmed.length >= minLength && trimmed.length <= maxLength && trimmed.length > 0;
}

export function validateContactForm(data: {
	name: string;
	email: string;
	inquiryType?: string;
	message: string;
}): ValidationResult {
	const errors: Record<string, string> = {};

	// Check name has at least 2 non-whitespace characters
	const trimmedName = data.name.trim();
	if (trimmedName.length < 2) {
		errors.name = 'Name must be at least 2 characters';
	} else if (data.name.length > MAX_NAME_LENGTH) {
		errors.name = `Name must be less than ${MAX_NAME_LENGTH} characters`;
	}

	if (!validateEmail(data.email)) {
		errors.email = 'Please enter a valid email address';
	}

	if (data.inquiryType && !VALID_INQUIRY_TYPES.includes(data.inquiryType as typeof VALID_INQUIRY_TYPES[number])) {
		errors.inquiryType = 'Invalid inquiry type';
	}

	if (!validateRequired(data.message, 10, MAX_MESSAGE_LENGTH)) {
		if (data.message.length > MAX_MESSAGE_LENGTH) {
			errors.message = `Message must be less than ${MAX_MESSAGE_LENGTH} characters`;
		} else {
			errors.message = 'Message must be at least 10 characters';
		}
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors
	};
}

export function validateNewsletterForm(email: string): ValidationResult {
	const errors: Record<string, string> = {};

	if (!validateEmail(email)) {
		errors.email = 'Please enter a valid email address';
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors
	};
}

export function validateEnrollmentForm(data: {
	name: string;
	email: string;
	courses: string[];
	experienceLevel: string;
	validCourseSlugs?: string[];
	message?: string;
}): ValidationResult {
	const errors: Record<string, string> = {};

	// Check name has at least 2 non-whitespace characters
	const trimmedName = data.name.trim();
	if (trimmedName.length < 2) {
		errors.name = 'Name must be at least 2 characters';
	} else if (data.name.length > MAX_NAME_LENGTH) {
		errors.name = `Name must be less than ${MAX_NAME_LENGTH} characters`;
	}

	if (!validateEmail(data.email)) {
		errors.email = 'Please enter a valid email address';
	}

	if (data.courses.length === 0) {
		errors.courses = 'Please select at least one course';
	}

	// Validate course slugs against known courses if provided
	if (data.validCourseSlugs && data.courses.length > 0) {
		const invalidCourses = data.courses.filter(c => !data.validCourseSlugs!.includes(c));
		if (invalidCourses.length > 0) {
			errors.courses = 'Invalid course selection';
		}
	}

	if (!data.experienceLevel) {
		errors.experienceLevel = 'Please select your experience level';
	} else if (!VALID_EXPERIENCE_LEVELS.includes(data.experienceLevel as typeof VALID_EXPERIENCE_LEVELS[number])) {
		errors.experienceLevel = 'Invalid experience level';
	}

	// Optional message validation
	if (data.message && data.message.length > MAX_MESSAGE_LENGTH) {
		errors.message = `Message must be less than ${MAX_MESSAGE_LENGTH} characters`;
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors
	};
}

export function validateConsultationForm(data: {
	name: string;
	email: string;
	projectType: string;
	description: string;
}): ValidationResult {
	const errors: Record<string, string> = {};

	// Check name has at least 2 non-whitespace characters
	const trimmedName = data.name.trim();
	if (trimmedName.length < 2) {
		errors.name = 'Name must be at least 2 characters';
	} else if (data.name.length > MAX_NAME_LENGTH) {
		errors.name = `Name must be less than ${MAX_NAME_LENGTH} characters`;
	}

	if (!validateEmail(data.email)) {
		if (data.email.length > MAX_EMAIL_LENGTH) {
			errors.email = `Email must be less than ${MAX_EMAIL_LENGTH} characters`;
		} else {
			errors.email = 'Please enter a valid email address';
		}
	}

	if (!data.projectType) {
		errors.projectType = 'Please select a project type';
	} else if (!VALID_PROJECT_TYPES.includes(data.projectType as typeof VALID_PROJECT_TYPES[number])) {
		errors.projectType = 'Invalid project type';
	}

	if (!validateRequired(data.description, 10, MAX_MESSAGE_LENGTH)) {
		if (data.description.length > MAX_MESSAGE_LENGTH) {
			errors.description = `Description must be less than ${MAX_MESSAGE_LENGTH} characters`;
		} else {
			errors.description = 'Description must be at least 10 characters';
		}
	}

	return {
		valid: Object.keys(errors).length === 0,
		errors
	};
}
